import { useEffect, useRef } from 'react';

export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ripples = useRef<Array<{ x: number; y: number; radius: number; maxRadius: number }>>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      ripples.current.push({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        radius: 0,
        maxRadius: 150,
      });
    };

    window.addEventListener('resize', handleResize);
    canvas.addEventListener('click', handleClick);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ripples.current = ripples.current.filter((ripple) => {
        ripple.radius += 2;

        if (ripple.radius > ripple.maxRadius) {
          return false;
        }

        const opacity = 1 - ripple.radius / ripple.maxRadius;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(14, 165, 233, ${opacity * 0.5})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        return true;
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/a413a0d9ffc31ccad508bcdd397ff6df.jpg"
          alt="Lake Guntersville"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-10 cursor-pointer"
        style={{ pointerEvents: 'auto' }}
      />

      <div className="relative z-20 h-full flex items-center justify-center text-center px-6">
        <div className="max-w-4xl w-full">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 drop-shadow-lg font-serif">
            The Way at the Lake
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-8 drop-shadow-md">
            Discover the tranquil beauty of nature's masterpiece
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => document.getElementById('activities')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-sky-600 text-white px-8 py-3 rounded-full text-base font-medium hover:bg-sky-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Explore Activities
            </button>
            <button
              onClick={() => document.getElementById('visitor-info')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/20 backdrop-blur-sm text-white px-8 py-3 rounded-full text-base font-medium hover:bg-white/30 transition-all hover:scale-105 border border-white/30 whitespace-nowrap cursor-pointer"
            >
              Plan Your Visit
            </button>
          </div>
          <p className="text-sm text-white/80 mt-8 drop-shadow-md">
            Click anywhere to create ripples
          </p>
        </div>
      </div>
    </section>
  );
}
